import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getProviderInsurances, createProviderLicense, CreateProviderLicenseInput, deleteProviderLicense, getMentalHealthRoles, getProviderLicenses, ProviderInsurance, patchLicense, LicenseInput, getProviderMissingPayers, getProviderLicensesCollabAgreements } from '@uplift/api';
import { BaseQueryOpts } from './baseTypes';
import groupBy from 'lodash/groupBy';
import { providerQueryKeys } from './provider';


export const useMentalHealthRolesQuery = (opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: [ 'mentalHealthRoles' ],
    queryFn: getMentalHealthRoles,
    select: response => response.data,
    ...opts,
  });
};


export const useCreateProviderLicenseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ providerId, ...args }: CreateProviderLicenseInput & { providerId: string }) => {
      return await createProviderLicense(providerId, args);
    },
    onSuccess: async (_, { providerId }) => {
      await Promise.all([
        queryClient.invalidateQueries(providerQueryKeys.licenses(providerId)),
        queryClient.invalidateQueries(providerQueryKeys.insurances(providerId)),
      ]);
    },
  });
};


export const useUpdateProviderLicenseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ providerId, licenseId, ...args }: LicenseInput & { providerId: string; licenseId: string }) => {
      return await patchLicense(providerId, licenseId, args);
    },
    onSuccess: async (_, { providerId }) => {
      await queryClient.invalidateQueries(providerQueryKeys.licenses(providerId));
    },
  });
};


export const useProviderLicensesQuery = (providerId: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: providerQueryKeys.licenses(providerId),
    queryFn: async () => getProviderLicenses(providerId),
    select: response => response.data,
    ...opts,
  });
};

export const useProviderLicensesCollabAgreementsQuery = (providerId: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: providerQueryKeys.licensesCollabAgreements(providerId),
    queryFn: async () => getProviderLicensesCollabAgreements(providerId),
    select: response => response.data,
    ...opts,
  });
};

export const useProviderMissingPayersQuery = (providerId: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: providerQueryKeys.missingPayers(providerId),
    queryFn: async () => getProviderMissingPayers(providerId),
    select: response => response.data,
    ...opts,
  });
};


export const useDeleteProviderLicenseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ providerId, licenseId }: { providerId: string; licenseId: string }) => {
      return await deleteProviderLicense(providerId, licenseId);
    },
    onSuccess: async (_, { providerId }) => {
      await queryClient.invalidateQueries(providerQueryKeys.licenses(providerId));
    },
  });
};


export const useProviderInsurancesQuery = (providerId: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: [ 'user', providerId, 'insurances' ],
    queryFn: async () => getProviderInsurances(providerId),
    select: response => groupBy(response.data, 'stateId') as Record<string, ProviderInsurance[]>,
    ...opts,
  });
};
